import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import AddOrderCustomOption from '../AddOrderCustomOption';
import { useLoading } from '@/context/LoadingContext';
import { getAllService } from '@/api/service-regist/getAllService';
import { useDispatch } from 'react-redux';
import { service } from '@/stores/slices/service-regist/serviceSlices';
import { useAppSelector } from '@/stores/hooks';
import { GetPartnerServiceOptionsResponse } from '@/api/type/types';
import BasicServiceOptions from './BasicServiceOptions';
import { putServiceOptions } from '@/api/service-regist/putServiceOptions';
import { LOCAL_STORAGE_KEYS } from '@/constants/storage';
import DesignServiceToggleButton from '@/components/button/DesignServiceToggleButton';
import { useTranslation } from 'react-i18next';
import { MultiLineText } from '@/components/text/MultiLineText';
import { SERVICE_TYPE } from '@/api/type/commonTypes';
import ToggleButton from '@/components/button/ToggleButton';

interface OrderOptionProps {
  data: GetPartnerServiceOptionsResponse;
  desc: string;
  serviceType: SERVICE_TYPE;
  imgUrl: string;
}
const OrderOption = ({ data, desc, serviceType, imgUrl }: OrderOptionProps) => {
  const [isDesignServiceOn, setIsDesignServiceOn] = useState<boolean>(
    data.designOnly
  );
  const { putPartnerServiceOptions: putData } = useAppSelector(
    (state) => state.service
  );
  const [isEdited, setIsEdited] = useState<boolean>(
    putData ? putData[serviceType]?.serviceType === data.serviceType : false
  );
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  /** 디자인 서비스 */
  const onClickDesignServiceToggle = useCallback(() => {
    setIsDesignServiceOn((prev) => !prev);
  }, []);

  const onClickEditBtn = useCallback(() => {
    setIsEdited(true);
    dispatch(
      service.setPutPartnerServiceOptions({
        serviceType: serviceType,
        data: data,
      })
    );
  }, [data, dispatch, serviceType]);

  console.log('putData', putData);
  const onClickSaveBtn = useCallback(async () => {
    startLoading();
    if (!putData) return;
    try {
      const res = await putServiceOptions({
        parentServiceOptionIds:
          putData[serviceType]?.options.map((o) => o.id) ?? [],
        customOptionNames:
          putData[serviceType]?.customOptions.map((c) => c.name) ?? [],
        designOnly: isDesignServiceOn,
        partnerServiceId: data.id,
      });
      if (res.isSuccess) {
        dispatch(
          service.setPutPartnerServiceOptions({
            serviceType: serviceType,
            data: null,
          })
        );
        dispatch(service.setPartnerServiceOptions(res.data));
      }
    } finally {
      stopLoading();
    }
    setIsEdited(false);
  }, [
    data.id,
    dispatch,
    isDesignServiceOn,
    putData,
    serviceType,
    startLoading,
    stopLoading,
  ]);

  const fetchAllServices = useCallback(async () => {
    startLoading();
    try {
      const res = await getAllService();
      if (res.isSuccess) {
        dispatch(service.setAllService(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [dispatch, startLoading, stopLoading]);

  useEffect(() => {
    fetchAllServices();
  }, [fetchAllServices]);

  const onClickCancel = useCallback(() => {
    console.log('data', data);
    dispatch(
      service.setServiceActive({
        serviceType: data.serviceType,
        active: data.active,
      })
    );
    dispatch(
      service.setPutPartnerServiceOptions({
        serviceType: serviceType,
        data: null,
      })
    );
    setIsDesignServiceOn(data.designOnly);
    setIsEdited(false);
  }, [data, dispatch, serviceType]);

  const optionData = useMemo(() => {
    const currentPutData = putData?.[serviceType];
    if (currentPutData) {
      return currentPutData;
    }

    return data;
  }, [data, putData, serviceType]);

  const role = localStorage.getItem(LOCAL_STORAGE_KEYS.ROLE_TYPE);

  const isDisabled = useMemo(() => {
    const options = putData[serviceType]?.options;
    return !options || options.length === 0;
  }, [putData, serviceType]);

  return (
    <Container>
      <Desc data-en={i18n.language === 'en'}>
        <CategoryImg src={imgUrl} alt={imgUrl} />
        <MultiLineText translationKey={desc} />
      </Desc>
      <BottomContainer>
        <OptionSection>
          <DescBox>
            <TitleWrap>{t('defaultOptions')}</TitleWrap>
            <OptionDesc>
              <MultiLineText
                translationKey={t('clickTheOptionButtonOnTheRightToActivateIt')}
              />
              <span>
                <MultiLineText
                  translationKey={t(
                    'allDefaultOptionsCanBeSelectedSimultaneously'
                  )}
                />
              </span>
              <MultiLineText
                translationKey={t('afterSelectingTheDefaultOptions')}
              />
            </OptionDesc>
          </DescBox>
          <RightSection>
            <BasicServiceOptions
              data={data}
              basicData={optionData?.options ?? []}
              isEdited={isEdited}
              serviceType={serviceType}
            />
          </RightSection>
        </OptionSection>
        <OptionSection>
          <DescBox>
            <TitleWrap>{t('addService')}</TitleWrap>
            <OptionDesc>
              <MultiLineText
                translationKey={t('ifYouNeedToAddServiceOptions')}
              />
              <span>
                <MultiLineText translationKey={t('afterAddingServices')} />
              </span>
            </OptionDesc>
          </DescBox>
          <RightSection>
            <AddOrderCustomOption
              isEdited={isEdited}
              customData={optionData?.customOptions ?? []}
              serviceType={serviceType}
            />
          </RightSection>
        </OptionSection>
        <OptionSection>
          <DescBox>
            <DesignTitleSection>
              <TitleWrap data-designonly={true}>{t('designService')}</TitleWrap>
            </DesignTitleSection>
            <OptionDesc>
              <MultiLineText
                translationKey={t('theDesignServceProvidesDesignOnlyWithout')}
              />
              <span>
                <MultiLineText
                  translationKey={t(
                    'ifYouWouldLikeToOfferProstheticDeisgnService'
                  )}
                />
              </span>
            </OptionDesc>
            <DesignServiceToggleButton
              isOn={isDesignServiceOn}
              onClickToggle={onClickDesignServiceToggle}
              disabled={!isEdited || role === 'PLANNER'}
              isEdit={isEdited}
            />
          </DescBox>
          <RightSection></RightSection>
        </OptionSection>
      </BottomContainer>
      <ButtonSection>
        {!isEdited && (
          <SaveButton onClick={onClickEditBtn}>{t('modify')}</SaveButton>
        )}
        {isEdited && (
          <>
            <SaveButton onClick={onClickSaveBtn} disabled={isDisabled}>
              {t('save')}
            </SaveButton>
            <SaveButton onClick={onClickCancel} data-cancel={true}>
              {t('cancel')}
            </SaveButton>
          </>
        )}
      </ButtonSection>
    </Container>
  );
};

export default OrderOption;

const Container = styled.div`
  padding: 37px 45px 33px 47px;
  border-radius: 0px 0px 8px 8px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  background: #fdfdff;
  display: flex;
  flex-direction: column;
`;

const Desc = styled.div`
  font-size: 15px;
  line-height: 120%;
  color: #000000;
  text-align: start;
  display: flex;
  align-items: center;
  gap: 11px;
`;

const RightSection = styled.div`
  flex: 1.5;
`;

const DescBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 15px;
  flex: 0.5;
  min-width: 320px;
`;

const DesignTitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
`;

const TitleWrap = styled.div`
  font-size: 20px;
  line-height: 120%;
  color: #fff8f8;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 99px;
  background: #5b4be8;
  width: 160px;
  &[data-designonly='true'] {
    background: #df67e2;
  }
`;

const OptionDesc = styled.div`
  font-size: 14px;
  line-height: normal;
  color: #000000;
  text-align: start;
  flex-direction: column;
  gap: 5px;
  display: flex;
`;

const ButtonSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
`;

const SaveButton = styled.button`
  height: 40px;
  width: 136px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 99px;
  background: #5b4be8;
  color: #ffffff;
  font-size: 16px;
  line-height: 140%;
  &[data-cancel='true'] {
    background: #777777;
  }
  &:disabled {
    cursor: auto;
    opacity: 0.7;
  }
`;

const OptionSection = styled.div`
  display: flex;
  gap: 65px;
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
  margin: 41px 0 74px;
`;

const CategoryImg = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 8px;
`;
